/**
 * Payroll Submissions — the tab.
 *
 * State machine: pick a month → (no run) upload → review → confirm → submit →
 * results, with a poll while posting and per-journal edit/retry on failure.
 * Readers see everything read-only; only admins get the upload and submit paths.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import type { MeResponse } from "../api";
import {
  getYears, getRunForPeriod, getRun, previewRun, commitRun, deleteRun, validateRun, submitRun, retryRun,
  type FinancialYear, type PayMonth, type Run, type ValidateResult, type ImportPreview, type DeptEdit,
} from "../payroll/api";
import MonthPills from "./payroll/MonthPills";
import RunReview from "./payroll/RunReview";
import ImportReview from "./payroll/ImportReview";
import ConfirmModal from "./payroll/ConfirmModal";
import SubmitProgress from "./payroll/SubmitProgress";
import ResultsTable from "./payroll/ResultsTable";

export default function PayrollSubmissions({ me }: { me: MeResponse }) {
  const [years, setYears] = useState<FinancialYear[] | null>(null);
  const [fyCode, setFyCode] = useState<string | null>(null);
  const [month, setMonth] = useState<PayMonth | null>(null);
  const [run, setRun] = useState<Run | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState<null | "upload" | "validate" | "submit" | "retry">(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [dryRun, setDryRun] = useState<ValidateResult | null>(null);
  const [confirming, setConfirming] = useState(false);
  // The department-review step: holds the picked file + the server's preview
  // while Simon resolves any new/mis-mapped departments before we commit.
  const [review, setReview] = useState<{ file: File; preview: ImportPreview } | null>(null);
  const fileInput = useRef<HTMLInputElement>(null);

  /* ---- Years, then default to the current month ---- */
  useEffect(() => {
    let dead = false;
    void (async () => {
      try {
        const ys = await getYears();
        if (dead) return;
        setYears(ys);
        const current = ys.find((y) => y.status === "current") ?? ys[0];
        setFyCode(current?.fyCode ?? null);
        setMonth(defaultMonth(current));
      } catch (e) {
        if (!dead) setError(msg(e));
      } finally {
        if (!dead) setLoading(false);
      }
    })();
    return () => { dead = true; };
  }, []);

  /* ---- Load the run whenever the month changes ---- */
  useEffect(() => {
    if (!month) return;
    let dead = false;
    setRun(null);
    setDryRun(null);
    setError(null);
    setNotice(null);
    setReview(null);
    void (async () => {
      try {
        const r = await getRunForPeriod(month.periodCode);
        if (!dead) setRun(r);
      } catch (e) {
        if (!dead) setError(msg(e));
      }
    })();
    return () => { dead = true; };
  }, [month?.periodCode]);

  /* ---- Poll while journals are being posted ---- */
  useEffect(() => {
    if (!run || busy !== "submit") return;
    const t = setInterval(() => {
      void getRun(run.id).then(setRun).catch(() => {});
    }, 1200);
    return () => clearInterval(t);
  }, [run?.id, busy]);

  const refreshYears = useCallback(() => { void getYears().then(setYears).catch(() => {}); }, []);

  async function commit(file: File, edits: DeptEdit[], overwrite: boolean) {
    if (!month) return;
    setBusy("upload");
    setError(null);
    setNotice(null);
    try {
      const r = await commitRun(file, month.periodCode, edits, overwrite);
      setReview(null);
      setRun(r);
      setNotice(`${file.name} parsed — ${r.journals.length} journals ready for review.`);
      refreshYears();
    } catch (e) {
      setError(msg(e));
    } finally {
      setBusy(null);
    }
  }

  async function onFile(file: File | null) {
    if (!file || !month) return;
    setBusy("upload");
    setError(null);
    setNotice(null);
    setReview(null);
    try {
      // Always preview first. If every department maps cleanly, commit straight
      // through; otherwise hand off to the review card holding the file.
      const preview = await previewRun(file, month.periodCode);
      if (preview.reconciliation.clean) {
        await commit(file, [], true);
      } else {
        setReview({ file, preview });
      }
    } catch (e) {
      setError(msg(e));
    } finally {
      setBusy(null);
      if (fileInput.current) fileInput.current.value = "";
    }
  }

  async function onReplace() {
    if (!run) return;
    if (!window.confirm("Discard this parsed report and upload a different file?")) return;
    try {
      await deleteRun(run.id);
      setRun(null);
      setDryRun(null);
      refreshYears();
    } catch (e) { setError(msg(e)); }
  }

  async function onDryRun() {
    if (!run) return;
    setBusy("validate");
    setError(null);
    try {
      setDryRun(await validateRun(run.id));
    } catch (e) { setError(msg(e)); } finally { setBusy(null); }
  }

  async function onSubmit() {
    if (!run) return;
    setConfirming(false);
    setBusy("submit");
    setError(null);
    try {
      const res = await submitRun(run.id);
      setRun(res.run);
      setNotice(res.ok
        ? `All ${res.posted} journals created as unposted drafts in iplicit.`
        : `${res.posted} created, ${res.failed} failed — edit the failures below and retry.`);
      refreshYears();
    } catch (e) { setError(msg(e)); } finally { setBusy(null); }
  }

  async function onRetryAll() {
    if (!run) return;
    setBusy("retry");
    setError(null);
    try {
      const res = await retryRun(run.id);
      setRun(res.run);
      setNotice(res.ok ? "All remaining journals created." : `${res.failed} still failing.`);
      refreshYears();
    } catch (e) { setError(msg(e)); } finally { setBusy(null); }
  }

  if (loading) return <section className="panel"><p className="muted">Loading payroll calendar…</p></section>;

  const posting = busy === "submit" || run?.status === "submitting";
  const anyPosted = !!run?.journals.some((j) => j.status === "draft_created");
  const failed = run?.journals.filter((j) => j.status === "failed") ?? [];
  const showResults = !!run && (anyPosted || failed.length > 0) && !posting;

  return (
    <section className="panel">
      <header className="panel__head">
        <div>
          <h2>Payroll Submissions</h2>
          <p className="panel__sub">
            Upload the IRIS payroll report, review the departmental totals, then post one draft
            manual journal per department into iplicit.
          </p>
        </div>
        <span className="entity-chip">NYMR02 · PLC</span>
      </header>

      <MonthPills
        years={years ?? []}
        fyCode={fyCode}
        month={month}
        onPickYear={(fy) => {
          setFyCode(fy);
          setMonth(defaultMonth((years ?? []).find((y) => y.fyCode === fy)));
        }}
        onPickMonth={setMonth}
      />

      {error && <div className="alert alert--error">{error}</div>}
      {notice && !error && <div className="alert alert--ok">{notice}</div>}

      {review && (
        <ImportReview
          preview={review.preview}
          busy={busy === "upload"}
          onApply={(edits, overwrite) => void commit(review.file, edits, overwrite)}
          onCancel={() => { setReview(null); setNotice(null); }}
        />
      )}

      {!run && !review && (
        <div className="empty payroll-drop">
          <p><strong>No payroll report for {month?.monthLabel ?? "this month"} yet</strong></p>
          <p className="muted">
            The IRIS PDF is parsed on the server and stored against period{" "}
            <span className="mono">{month?.periodCode}</span>.
          </p>
          {me.isAdmin ? (
            <>
              <input
                ref={fileInput}
                type="file"
                accept="application/pdf"
                hidden
                onChange={(e) => void onFile(e.target.files?.[0] ?? null)}
              />
              <button className="btn btn--primary" disabled={busy === "upload"} onClick={() => fileInput.current?.click()}>
                {busy === "upload" ? "Parsing…" : "Choose payroll PDF"}
              </button>
              <p className="muted">PDF only · max 10 MB</p>
            </>
          ) : (
            <p className="muted">Ask a Finance admin to upload it.</p>
          )}
        </div>
      )}

      {run && (
        <div className="payroll-run">
          <RunReview run={run} canEdit={me.isAdmin} onReplace={onReplace} />

          {!posting && !showResults && me.isAdmin && (
            <div className="payroll-actions">
              <div className="muted">
                {dryRun
                  ? dryRun.ok
                    ? `✓ Dry run passed — iplicit validated ${dryRun.journals} journals and ${dryRun.lines} lines without saving anything.`
                    : `✕ Dry run found ${dryRun.problems.length} problem(s): ${dryRun.problems.map((p) => `${p.department}: ${p.error}`).join("; ")}`
                  : "A dry run validates every journal against iplicit without creating anything."}
              </div>
              <div className="payroll-actions__buttons">
                <button className="btn" disabled={busy === "validate"} onClick={() => void onDryRun()}>
                  {busy === "validate" ? "Validating…" : "Dry run (validate only)"}
                </button>
                <button className="btn btn--primary" onClick={() => setConfirming(true)}>Submit to iplicit</button>
              </div>
            </div>
          )}

          {posting && <SubmitProgress run={run} />}

          {showResults && (
            <ResultsTable
              run={run}
              canEdit={me.isAdmin}
              busy={busy === "retry"}
              onRunChange={setRun}
              onRetryAll={() => void onRetryAll()}
            />
          )}
        </div>
      )}

      {confirming && run && (
        <ConfirmModal
          run={run}
          dryRun={dryRun}
          onCancel={() => setConfirming(false)}
          onConfirm={() => void onSubmit()}
        />
      )}
    </section>
  );
}

function defaultMonth(year?: FinancialYear): PayMonth | null {
  if (!year) return null;
  const now = new Date();
  const label = `${now.toLocaleString("en-GB", { month: "short" })} ${String(now.getFullYear() % 100).padStart(2, "0")}`;
  return (
    year.months.find((m) => m.monthLabel === label) ??
    [...year.months].reverse().find((m) => m.status !== "not_started") ??
    year.months[0] ??
    null
  );
}

const msg = (e: unknown) => (e instanceof Error ? e.message : String(e));
